<script setup lang="ts">
import {
  PhBlueprint as BlueprintIcon,
  PhCheckCircle as CheckCircleIcon,
  PhCube as CubeIcon,
  PhFactory as FactoryIcon,
  PhFileArrowUp as FileArrowUpIcon,
  PhHandCoins as HandCoinsIcon,
  PhRuler as RulerIcon,
  PhWechatLogo as WechatLogoIcon,
} from '@phosphor-icons/vue'

const designSteps = [
  {
    title: '提交设计图和需求',
    description: '通过微信发送设计图或参考图，说明柜体位置、所在城市和特殊要求。',
    icon: FileArrowUpIcon,
  },
  {
    title: '48 小时内看方案',
    description: '我们先制作 CAD 与渲染效果图，帮你确认方向，此时不作为最终生产尺寸。',
    icon: CubeIcon,
  },
  {
    title: '上门量房',
    description: '方案方向确认后，再安排师傅现场测量，记录墙面、障碍物和安装条件。',
    icon: RulerIcon,
  },
  {
    title: '出具正式报价',
    description: '结合量房结果、材料、五金和施工条件，给出可逐项核对的明细报价。',
    icon: HandCoinsIcon,
  },
  {
    title: '完成四部分确认',
    description: '一起确认尺寸、方案、材质五金和生产规则，任何改动都需要重新确认。',
    icon: CheckCircleIcon,
  },
  {
    title: '确认后生产',
    description: '四部分资料和付款状态确认无误后，方案锁定并进入生产。',
    icon: FactoryIcon,
  },
]
</script>

<template>
  <section class="page-hero page-hero--custom-design">
    <div class="container page-hero__inner">
      <div>
        <span class="eyebrow">专属方案</span>
        <h1>来图定制</h1>
        <p>适合已有设计图、有特殊需求，或现有款式无法满足的家庭。</p>
      </div>
      <BlueprintIcon :size="88" weight="duotone" aria-hidden="true" />
    </div>
  </section>

  <section class="section" aria-labelledby="custom-design-flow-title">
    <div class="container">
      <div class="section-heading">
        <h2 id="custom-design-flow-title">从一张图，到可生产的方案</h2>
        <p>先看图纸和需求，再进入量房与报价。每一步确认清楚后，才会继续往下走。</p>
      </div>

      <ol class="custom-design-flow" aria-label="来图定制流程">
        <li v-for="step in designSteps" :key="step.title">
          <span class="custom-design-flow__icon" aria-hidden="true">
            <component :is="step.icon" :size="23" weight="duotone" />
          </span>
          <h3>{{ step.title }}</h3>
          <p>{{ step.description }}</p>
        </li>
      </ol>
    </div>
  </section>

  <section class="section section--subtle" aria-labelledby="custom-design-confirmation-title">
    <div class="container confirmation-explainer">
      <div class="section-heading">
        <h2 id="custom-design-confirmation-title">生产前，需要你确认四部分资料</h2>
        <p>你确认的版本，就是后续报价、付款和生产的依据。</p>
      </div>

      <div class="confirmation-quadrants">
        <article>
          <span>尺寸确认</span>
          <h3>以现场量房报告为准</h3>
          <p>确认柜体尺寸、墙面条件、障碍物、入户和安装条件。</p>
        </article>
        <article>
          <span>方案确认</span>
          <h3>看清最终图纸和版本</h3>
          <p>确认正视图、侧视图、内部结构、开门方向和收口方式。</p>
        </article>
        <article>
          <span>材质五金</span>
          <h3>编号、品牌和型号都要对上</h3>
          <p>确认颜色编号、板材信息、五金品牌、型号和整套配置。</p>
        </article>
        <article>
          <span>生产规则</span>
          <h3>交期与改动边界事先说清</h3>
          <p>确认预计交期、改动费用、生产冻结点和退款保障规则。</p>
        </article>
      </div>
    </div>
  </section>

  <section class="section section--compact" aria-labelledby="custom-design-wechat-title">
    <div class="container assurance-cta custom-design-cta">
      <div>
        <WechatLogoIcon :size="34" weight="duotone" aria-hidden="true" />
        <h2 id="custom-design-wechat-title">通过微信提交图纸</h2>
        <p>请一起发送设计图或参考图、所在省市和特殊需求。资料越完整，方案沟通越快。</p>
      </div>
      <div class="custom-design-cta__checklist" role="note" aria-label="微信提交建议">
        <strong>建议备注</strong>
        <span>来图定制、姓名、所在城市</span>
      </div>
    </div>
  </section>
</template>

<style scoped>
.custom-design-flow {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 28px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.custom-design-flow li {
  position: relative;
  min-width: 0;
}

.custom-design-flow li:not(:last-child)::after {
  content: "→";
  position: absolute;
  top: 8px;
  right: -22px;
  color: var(--line-strong);
  font-size: 1.15rem;
  font-weight: 720;
}

.custom-design-flow__icon {
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  color: var(--accent);
  background: var(--accent-soft);
  border: 1px solid color-mix(in srgb, var(--accent) 24%, var(--line));
  border-radius: var(--radius-control);
}

.custom-design-flow h3 {
  margin: 18px 0 8px;
  font-size: 1.04rem;
}

.custom-design-flow p {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.65;
}

.custom-design-cta__checklist {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
  max-width: 290px;
  padding: 14px 18px;
  color: var(--accent);
  background: var(--accent-contrast);
  border: 1px solid color-mix(in srgb, var(--accent-contrast) 70%, transparent);
  border-radius: var(--radius-control);
  line-height: 1.45;
}

.custom-design-cta__checklist strong {
  font-size: 0.82rem;
}

.custom-design-cta__checklist span {
  color: color-mix(in srgb, var(--accent) 82%, var(--text));
  font-size: 0.86rem;
}

@media (max-width: 900px) {
  .custom-design-flow {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .custom-design-flow li {
    display: grid;
    grid-template-columns: 42px minmax(0, 1fr);
    column-gap: 14px;
    padding-bottom: 34px;
  }

  .custom-design-flow li:not(:last-child)::after {
    content: "↓";
    top: auto;
    right: auto;
    bottom: 5px;
    left: 13px;
  }

  .custom-design-flow__icon {
    grid-row: 1 / 3;
  }

  .custom-design-flow h3 {
    margin: 0 0 5px;
  }

  .custom-design-flow p {
    grid-column: 2;
  }
}

@media (max-width: 720px) {
  .custom-design-cta__checklist {
    width: 100%;
    max-width: none;
  }
}
</style>
